import { useCallback, useEffect, useMemo, useRef, createElement } from "react";
import type { ReactElement } from "react";
import { Grid, AgGridReact, AgGrid, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { EntitiesByBillingGroupDataSource } from "~/pages/Billing/ClientSpecificView/EntitiesCard/EntitiesByBillingGroupGrid/entitiesByBillingGroupDataSource";
import { LoadingCellRenderer } from "~/pages/Billing/components/LoadingCellRenderer";
import { formatDateForApi } from "~/pages/Billing/contexts/BillingDateContext";
import { formatDate } from "~/pages/Billing/utils";
import { BulkActionsPanel } from "../BulkActionsPanel";
import {
  getSelectionType,
  getRowSelectability,
  isNotAssignedGroupNode,
  filterNotAssignedGroup,
} from "../bulkActionsUtils";
import {
  MIXED_CHANGE_TYPES_ERROR,
  NOT_ASSIGNED_LABEL,
  NOT_ASSIGNED_RAW_ID,
  STANDALONE_LABEL,
  getReasonCodeLabel,
} from "../constants";
import { CaseTypeDisplay, EntitySelectionType } from "../types";
import {
  buildEumValueOverrideChange,
  buildEumCalcEditChange,
} from "../utils/eumProposedChangesBuilders";
import { isEumPendingChange } from "../utils/gpEumChangeItemBuilders";
import {
  getCaseEntitiesColumnDefs,
  getCaseEntitiesAutoGroupColumnDef,
} from "./caseEntitiesColDef";
import { gridContainer } from "./styles_vanilla.css";
import type { CaseEntityRow } from "./types";
import type {
  BulkActionChangeData,
  GridActions,
  ProposedChangeMetadata,
  StoredProposedChange,
} from "../types";
import type { IRowNode, SelectionChangedEvent } from "ag-grid-community";

const BULK_ACTIONS_PANEL_ID = "bulkActions";
const BILLING_PRODUCT_LINE_FILTER_MODEL = {
  jsqProductLine: {
    filterType: "set",
    values: ["Fund Administration", "Fund Services", "Deal Services"],
  },
};

export const getRowIdForCaseEntity = (data: CaseEntityRow): string => {
  if (data.type === "billingGroup") {
    return `bg-${data.billingGroupId}`;
  }
  return `entity-${data.entityId}`;
};

export const isCaseEntityServerSideGroup = (dataItem: CaseEntityRow): boolean =>
  dataItem.type === "billingGroup" && dataItem.childCount > 0;

export const getCaseEntityServerSideGroupKey = (dataItem: CaseEntityRow): string =>
  dataItem.billingGroupId;

const getCurrentBillingGroupName = (
  api: AgGrid.GridApi<CaseEntityRow>,
  entityId: number,
): string => {
  let billingGroupId: string | null = null;
  const groupNameLookup = new Map<string, string>();

  api.forEachNode((node) => {
    if (!node.data) {
      return;
    }
    if (node.data.type === "entity" && node.data.entityId === entityId && !billingGroupId) {
      billingGroupId = node.data.billingGroupId;
    }
    if (node.data.type === "billingGroup" && node.data.billingGroupName) {
      groupNameLookup.set(node.data.billingGroupId, node.data.billingGroupName);
    }
  });

  if (!billingGroupId || billingGroupId === NOT_ASSIGNED_RAW_ID) {
    return NOT_ASSIGNED_LABEL;
  }

  return groupNameLookup.get(billingGroupId) ?? NOT_ASSIGNED_LABEL;
};

export type CaseEntitiesGridProps = {
  isChecker: boolean;
  clientId: string;
  onEntityProposedChangeClick: (entityId: number, entityName: string | null) => void;
  onFamilyProposedChangeClick: (familyName: string | null) => void;
  onProposedChangeSubmit: (
    data: BulkActionChangeData,
  ) => Promise<{ familyName: string } | null>;
  onProposedChangeDetail?: (entityIds: number[], change: StoredProposedChange) => void;
  onEditFirstCapitalCallDateSubmit?: (
    entityIds: number[],
    entityNames: string[],
    date: Date,
  ) => void;
  wouldMixChangeTypes?: (incomingIsEum: boolean) => boolean;
  onGridActionsReady?: (actions: GridActions) => void;
  initialProposedChanges?: Map<number, number>;
  familyProposedChangeCounts?: Map<string, number>;
};

export const CaseEntitiesGrid = ({
  isChecker,
  clientId,
  onEntityProposedChangeClick,
  onFamilyProposedChangeClick,
  onProposedChangeSubmit,
  onProposedChangeDetail,
  onEditFirstCapitalCallDateSubmit,
  wouldMixChangeTypes,
  onGridActionsReady,
  initialProposedChanges,
  familyProposedChangeCounts,
}: CaseEntitiesGridProps): ReactElement => {
  const asOfDateString = useMemo(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDateForApi(yesterday);
  }, []);
  const gridApiRef = useRef<AgGrid.GridApi<CaseEntityRow> | null>(null);
  const isMaker = !isChecker;
  const selectionCategoryRef = useRef<EntitySelectionType>(EntitySelectionType.None);
  const notAssignedGroupSelectedRef = useRef(false);
  const selectedBillingGroupIdRef = useRef<string | null>(null);
  const isSafeCaseIdEnabled = useFeatureFlag("billingSafeCaseIdAndActiveInvestments", false);
  const familyProposedChangeCountsRef = useRef(familyProposedChangeCounts);
  familyProposedChangeCountsRef.current = familyProposedChangeCounts;

  const appliedEntityIdsRef = useRef(new Set<number>());

  useEffect(() => {
    appliedEntityIdsRef.current = new Set<number>();
  }, [initialProposedChanges]);

  const applyInitialProposedChanges = useCallback(() => {
    const api = gridApiRef.current;
    if (!api || !initialProposedChanges || initialProposedChanges.size === 0) {
      return;
    }
    let updated = false;
    api.forEachNode((node) => {
      if (node.data?.type === "entity" && typeof node.data.entityId === "number") {
        if (appliedEntityIdsRef.current.has(node.data.entityId)) {
          return;
        }
        const count = initialProposedChanges.get(node.data.entityId);
        if (typeof count === "number" && count > 0) {
          node.data.proposedChanges = count;
          appliedEntityIdsRef.current.add(node.data.entityId);
          updated = true;
        }
      }
    });
    if (updated) {
      api.refreshCells({ columns: ["proposedChanges"], force: true });
    }
  }, [initialProposedChanges]);

  const applyInitialProposedChangesRef = useRef(applyInitialProposedChanges);
  useEffect(() => {
    applyInitialProposedChangesRef.current = applyInitialProposedChanges;
  }, [applyInitialProposedChanges]);

  useEffect(() => {
    applyInitialProposedChanges();
  }, [applyInitialProposedChanges]);

  const columnDefs = useMemo(
    () =>
      getCaseEntitiesColumnDefs({
        onEntityProposedChangeClick,
        onFamilyProposedChangeClick,
        familyProposedChangeCountsRef,
        isSafeCaseIdEnabled,
      }),
    [onEntityProposedChangeClick, onFamilyProposedChangeClick, isSafeCaseIdEnabled],
  );

  const dataSource = useMemo(() => {
    if (!clientId) {
      return null;
    }
    return new EntitiesByBillingGroupDataSource(clientId, undefined, undefined, asOfDateString);
  }, [clientId, asOfDateString]);

  const isRowSelectable = useCallback((node: IRowNode<CaseEntityRow>): boolean => {
    if (
      !node.group &&
      node.data?.type === "entity" &&
      typeof node.data.proposedChanges === "number" &&
      node.data.proposedChanges > 0
    ) {
      return false;
    }
    if (node.group && node.data?.type === "billingGroup" && node.data.billingGroupName) {
      const count = familyProposedChangeCountsRef.current?.get(node.data.billingGroupName);
      if (typeof count === "number" && count > 0) {
        return false;
      }
    }
    return getRowSelectability(
      selectionCategoryRef.current,
      {
        group: node.group,
        data: node.data,
        key: node.key,
      },
      selectedBillingGroupIdRef.current,
    );
  }, []);

  const refreshSelectability = useCallback(
    (api: AgGrid.GridApi<CaseEntityRow>) => {
      api.setGridOption("isRowSelectable", (node: IRowNode<CaseEntityRow>) =>
        isRowSelectable(node),
      );
      api.redrawRows();
    },
    [isRowSelectable],
  );

  useEffect(() => {
    const api = gridApiRef.current;
    if (api && familyProposedChangeCounts) {
      api.refreshCells({ columns: ["proposedChanges"], force: true });
      refreshSelectability(api);
    }
  }, [familyProposedChangeCounts, refreshSelectability]);

  const deleteProposedChanges = useCallback(
    (entityIds: number[]) => {
      const api = gridApiRef.current;
      if (!api) {
        return;
      }
      const idSet = new Set(entityIds);
      api.forEachNode((node) => {
        if (
          node.data?.type === "entity" &&
          typeof node.data.entityId === "number" &&
          idSet.has(node.data.entityId) &&
          typeof node.data.proposedChanges === "number" &&
          node.data.proposedChanges > 0
        ) {
          node.data.proposedChanges = node.data.proposedChanges - 1;
        }
      });
      api.refreshCells({ columns: ["proposedChanges"], force: true });
      refreshSelectability(api);
    },
    [refreshSelectability],
  );

  const onSelectionChanged = useCallback(
    (event: SelectionChangedEvent<CaseEntityRow>) => {
      const selectedNodes = event.api.getSelectedNodes();

      const notAssignedGroup = selectedNodes.find((node) =>
        isNotAssignedGroupNode({ group: node.group, data: node.data, key: node.key }),
      );
      const wasSelected = notAssignedGroupSelectedRef.current;
      const isSelected = Boolean(notAssignedGroup);
      notAssignedGroupSelectedRef.current = isSelected;

      if (isSelected && !wasSelected) {
        notAssignedGroup?.childrenAfterGroup?.forEach((child) => {
          if (!child.group && !child.isSelected()) {
            child.setSelected(true);
          }
        });
        event.api.openToolPanel(BULK_ACTIONS_PANEL_ID);
        const newCategory = EntitySelectionType.UnassignedEntity;
        if (newCategory !== selectionCategoryRef.current) {
          selectionCategoryRef.current = newCategory;
          selectedBillingGroupIdRef.current = null;
          refreshSelectability(event.api);
        }
        return;
      }

      if (!isSelected && wasSelected) {
        event.api.forEachNode((node) => {
          if (!node.group && node.data?.billingGroupName === null && node.isSelected()) {
            node.setSelected(false);
          }
        });
        return;
      }

      if (selectedNodes.length > 0) {
        event.api.openToolPanel(BULK_ACTIONS_PANEL_ID);
      } else {
        event.api.closeToolPanel();
        selectedBillingGroupIdRef.current = null;
      }

      const mapped = filterNotAssignedGroup(
        selectedNodes.map((node) => ({ group: node.group, data: node.data, key: node.key })),
      );
      const newCategory = getSelectionType(mapped);
      const previousCategory = selectionCategoryRef.current;

      let newBillingGroupId: string | null | undefined;

      if (newCategory === EntitySelectionType.AssignedEntity) {
        newBillingGroupId =
          mapped.find(
            (node) => !node.group && node.data?.billingGroupId !== NOT_ASSIGNED_RAW_ID,
          )?.data?.billingGroupId ?? null;
      } else if (newCategory === EntitySelectionType.BillingGroup) {
        newBillingGroupId = mapped.find((node) => node.group)?.data?.billingGroupId ?? null;
      } else {
        selectedBillingGroupIdRef.current = null;
      }

      if (
        newBillingGroupId !== undefined &&
        newBillingGroupId !== selectedBillingGroupIdRef.current
      ) {
        selectedBillingGroupIdRef.current = newBillingGroupId;
        selectionCategoryRef.current = newCategory;
        refreshSelectability(event.api);
        return;
      }

      if (newCategory !== previousCategory) {
        selectionCategoryRef.current = newCategory;
        refreshSelectability(event.api);
      }
    },
    [refreshSelectability],
  );

  const handleGridProposedChangeSubmit = useCallback(
    async (data: BulkActionChangeData): Promise<{ familyName: string } | null> => {
      if (wouldMixChangeTypes?.(isEumPendingChange(data))) {
        toast.error(MIXED_CHANGE_TYPES_ERROR);
        return null;
      }
      const result = await onProposedChangeSubmit(data);
      const api = gridApiRef.current;
      if (!api) {
        return result;
      }
      if ("action" in data && (data.action === "rename" || data.action === "remove_group")) {
        return result;
      }
      const entityIds: number[] = (data as { entityIds: number[] }).entityIds;
      const entityIdSet = new Set(entityIds);
      api.forEachNode((node) => {
        if (
          node.data?.type === "entity" &&
          typeof node.data.entityId === "number" &&
          entityIdSet.has(node.data.entityId)
        ) {
          node.data.proposedChanges = (node.data.proposedChanges ?? 0) + 1;
        }
      });
      api.refreshCells({ columns: ["proposedChanges"], force: true });
      refreshSelectability(api);

      if (onProposedChangeDetail) {
        const currentBillingGroup = getCurrentBillingGroupName(api, entityIds[0]);

        if (result && "formValues" in data) {
          const { familyName } = result;
          onProposedChangeDetail(entityIds, {
            changeRequestId: "",
            billingGroupName: familyName,
            entityIds,
            detail: {
              label: "Billing group",
              currentValues: [currentBillingGroup],
              proposedValues: [familyName],
            },
          });
        } else if ("action" in data && data.action === "remove") {
          onProposedChangeDetail(entityIds, {
            changeRequestId: "",
            billingGroupName: data.billingGroupName,
            entityIds,
            detail: {
              label: "Billing group",
              currentValues: [currentBillingGroup],
              proposedValues: [NOT_ASSIGNED_LABEL],
            },
          });
        } else if ("action" in data && data.action === "standalone") {
          onProposedChangeDetail(entityIds, {
            changeRequestId: "",
            billingGroupName: STANDALONE_LABEL,
            entityIds,
            detail: {
              label: "Billing group",
              currentValues: [currentBillingGroup],
              proposedValues: [STANDALONE_LABEL],
            },
          });
        } else if ("action" in data && data.action === "eum_value_override") {
          const metadata: ProposedChangeMetadata = {
            reasonCode: getReasonCodeLabel(data.reasonCode),
            details: data.description,
            supportingDocumentName: data.file?.name,
          };
          onProposedChangeDetail(
            entityIds,
            buildEumValueOverrideChange({
              changeRequestId: "",
              entityId: entityIds[0],
              entityName: data.entityNames[0] ?? "",
              currentEumValue: data.currentEumValue,
              proposedEumValue: data.proposedEumValue,
              metadata,
            }),
          );
        } else if ("action" in data && data.action === "eum_calc_edit") {
          const metadata: ProposedChangeMetadata = {
            reasonCode: getReasonCodeLabel(data.reasonCode),
            details: data.description,
            supportingDocumentName: data.file?.name,
          };
          onProposedChangeDetail(
            entityIds,
            buildEumCalcEditChange({
              changeRequestId: "",
              entityId: entityIds[0],
              entityName: data.entityNames[0] ?? "",
              current: data.current,
              proposed: data.proposed,
              metadata,
            }),
          );
        }
      }
      return result;
    },
    [onProposedChangeSubmit, onProposedChangeDetail, refreshSelectability, wouldMixChangeTypes],
  );

  const handleOnGridReady = useCallback(
    (event: AgGrid.GridReadyEvent) => {
      gridApiRef.current = event.api;
      event.api.setFilterModel(BILLING_PRODUCT_LINE_FILTER_MODEL);
      if (dataSource) {
        event.api.setGridOption("serverSideDatasource", dataSource);
      }
      onGridActionsReady?.({ deleteProposedChanges });
    },
    [dataSource, onGridActionsReady, deleteProposedChanges],
  );

  const getRowId = useCallback(
    (params: AgGrid.GetRowIdParams<CaseEntityRow>) => getRowIdForCaseEntity(params.data),
    [],
  );

  const numericClientId = Number(clientId);

  const handleEditFirstCapitalCallDateSubmit = useCallback(
    (entityIds: number[], entityNames: string[], date: Date) => {
      if (wouldMixChangeTypes?.(false)) {
        toast.error(MIXED_CHANGE_TYPES_ERROR);
        return;
      }
      onEditFirstCapitalCallDateSubmit?.(entityIds, entityNames, date);

      const api = gridApiRef.current;
      if (!api) {
        return;
      }
      const entityIdSet = new Set(entityIds);
      const currentDates = new Set<string | null>();
      api.forEachNode((node) => {
        if (
          node.data?.type === "entity" &&
          typeof node.data.entityId === "number" &&
          entityIdSet.has(node.data.entityId)
        ) {
          node.data.proposedChanges = (node.data.proposedChanges ?? 0) + 1;
          currentDates.add(node.data.entityData?.firstCapitalCallDate ?? null);
        }
      });
      api.refreshCells({ columns: ["proposedChanges"], force: true });

      if (onProposedChangeDetail) {
        const formattedDate = formatDate(formatDateForApi(date));
        const hasUniformDate = currentDates.size === 1 && !currentDates.has(null);
        const rawCurrentDate = hasUniformDate ? [...currentDates][0]! : null;
        const currentValue = rawCurrentDate ? formatDate(rawCurrentDate) : "--";
        onProposedChangeDetail(entityIds, {
          changeRequestId: "",
          billingGroupName: "",
          entityIds,
          detail: {
            label: CaseTypeDisplay.FirstCapitalCallDate,
            currentValues: [currentValue],
            proposedValues: [formattedDate],
          },
        });
      }
    },
    [onEditFirstCapitalCallDateSubmit, onProposedChangeDetail, wouldMixChangeTypes],
  );

  const frameworkComponents = useMemo(() => {
    if (!isMaker) {
      return undefined;
    }
    return {
      bulkActionsPanel: (props: { api: AgGrid.GridApi }) =>
        createElement(BulkActionsPanel, {
          ...props,
          clientId: numericClientId,
          onProposedChangeSubmit: handleGridProposedChangeSubmit,
          onEditFirstCapitalCallDateSubmit: handleEditFirstCapitalCallDateSubmit,
        }),
    };
  }, [
    isMaker,
    numericClientId,
    handleGridProposedChangeSubmit,
    handleEditFirstCapitalCallDateSubmit,
  ]);

  const makerSideBar: AgGridReact.AgGridReactProps["sideBar"] = useMemo(
    () => ({
      position: "right" as const,
      toolPanels: [
        {
          id: BULK_ACTIONS_PANEL_ID,
          labelDefault: "Bulk actions",
          labelKey: "bulkActions",
          iconKey: "columns",
          toolPanel: "bulkActionsPanel",
        },
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
      ],
    }),
    [],
  );

  const checkerSideBar: AgGridReact.AgGridReactProps["sideBar"] = useMemo(
    () => ({
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
          },
        },
      ],
      defaultToolPanel: "",
    }),
    [],
  );

  const gridOptions: AgGridReact.AgGridReactProps<CaseEntityRow>["gridOptions"] = useMemo(
    () => ({
      rowModelType: "serverSide" as const,
      suppressAggFuncInHeader: true,
      suppressServerSideFullWidthLoadingRow: true,
      rowHeight: 48,
      suppressCellFocus: true,
      groupDisplayType: "singleColumn" as const,
      autoGroupColumnDef: getCaseEntitiesAutoGroupColumnDef(isMaker),
      isServerSideGroup: isCaseEntityServerSideGroup,
      getServerSideGroupKey: getCaseEntityServerSideGroupKey,
      defaultColDef: {
        flex: 1,
        menuTabs: ["generalMenuTab"],
      },
      onStoreRefreshed: () => applyInitialProposedChangesRef.current(),
      onModelUpdated: () => applyInitialProposedChangesRef.current(),
      ...(isMaker
        ? {
            rowSelection: "multiple" as const,
            groupSelectsChildren: false,
            suppressRowClickSelection: true,
            isRowSelectable,
            onSelectionChanged,
            sideBar: makerSideBar,
          }
        : {
            sideBar: checkerSideBar,
          }),
    }),
    [isMaker, isRowSelectable, onSelectionChanged, makerSideBar, checkerSideBar],
  );

  return (
    <div className={gridContainer} data-testid="case-entities-grid">
      <Grid<CaseEntityRow>
        gridOptions={gridOptions}
        columnDefs={columnDefs}
        onGridReady={handleOnGridReady}
        getRowId={getRowId}
        loadingCellRenderer={LoadingCellRenderer}
        components={frameworkComponents}
      />
    </div>
  );
};
